import { useContext, useMemo } from "react";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";

import { useQuery, useMutation, NetworkStatus } from "@apollo/client";
import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import {
  BsPlusCircleFill, 
  BsTrash
} from "react-icons/bs";

import { 
  Table,
  Pagination,
  ConfirmModal,
  Alert,
  RoleAccessButton
} from "@evos/components";
import { usePaginationHandlers } from "@evos/hooks";

import Header from "components/Header";
import { AuthContext } from "lib/context/auth";

import { 
  DELETE_USER,
  USERS,
} from "./gql";

import type {
  UserNode,
  UserConnection
} from "types";

function useAuth() {
  return useContext(AuthContext);
}

export default function UserList() {
  const auth = useAuth();
  const darkMode = auth?.darkMode ?? false;
  const userRoles = useMemo(() => auth?.user?.roles ?? [], [auth?.user?.roles]);

  type DeleteUserArguments = {
    id: string | null | undefined;
  }

  const { 
    data: usersData, 
    networkStatus: usersNetworkStatus, 
    error,
    fetchMore: fetchMoreUsers,
    refetch: refetchUsers
  } = useQuery<UserConnection>(USERS, {
    variables: {
      where: {
        id: {
          like: "@evos.com.au"
        }
      }
    },
  });

  const [deleteUser, {
    error: deleteUserError,
  }] = useMutation<{delete_platform_user: UserConnection["users"]}, DeleteUserArguments>(DELETE_USER, {
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      refetchUsers();
    }
  });

  const usersInitialLoading = usersNetworkStatus === NetworkStatus.loading;
  const usersFetchMoreLoading = usersNetworkStatus === NetworkStatus.fetchMore;

  const usersPageInfo = usersData?.users?.pageInfo;
  const totalCount = usersData?.users?.totalCount;

  const { 
    paginationState,
    handleNextPage,
    handlePrevPage,
    handlePageSize,
  } = usePaginationHandlers({
    pageInfoEndCursor: usersPageInfo?.endCursor,
    listOfCursors: usersData?.users?.edges?.map((c) => c.cursor),
    fetchMore: fetchMoreUsers,
    initialWhereArgument: {
      id: {
        like: "@evos.com.au"
      }
    }
  });

  const paginatedUsers = useMemo(() => (
    (usersData?.users?.edges?.map((edge) => edge.node).slice(
      (paginationState.pageSize * paginationState.pageNumber - paginationState.pageSize), (paginationState.pageSize * paginationState.pageNumber)))
  ), [usersData?.users?.edges, paginationState.pageNumber, paginationState.pageSize]);

  const usersTableData = paginatedUsers?.map((d) => ({
    id: d.id,
    email: d.email,
    roles: d.roles
  })) ?? [];

  const usersColumns = useMemo<Array<ColumnDef<UserNode>>>(
    () => [
      {
        header: "Email",
        accessorKey: "email"
      },
      { 
        header: "Roles",
        cell: ({ row }) => (
          row.original.roles?.join(", ") ?? "No roles"
        )
      },
      {
        header: "Actions",
        id: "actions",
        accessorFn: () => "actions",
        cell: ({row}) => (
          <div className="d-flex gap-2 text-nowrap">
            <ConfirmModal
              openModalElement={(onClickHandler) => (
                <RoleAccessButton
                  userRoles={userRoles}
                  expectedRole="EvosUserEditor"
                  tooltipId="deleteUser"
                  variant="outline-danger"
                  onClick={() => { 
                    onClickHandler();
                  }}
                >
                  <BsTrash /> Delete
                </RoleAccessButton>
              )}
              title="Delete user?"
              message={`Are you sure you want to delete user ${row?.original?.email}? This action cannot be undone.`}
              confirmButtonText="Delete user"
              confirmButtonVariant="danger"
              handleConfirm={() => { 
                if ( row?.original?.id != null ) {
                  deleteUser({variables: { id: row?.original?.id }}); 
                }
              }}
              size="md"
            />
          </div>
        )
      }
    ],
    [userRoles, deleteUser]
  );

  return (
    <div id="UserList">
      <Header />
      <Container>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 pt-4 pb-3">
          <div className="d-flex justify-content-start">
            <h1 className="mb-0" style={{fontSize: "1.7em"}}>EVOS Users</h1>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <RoleAccessButton 
              variant="primary"
              userRoles={userRoles}
              expectedRole="EvosUserEditor"
              tooltipId="addUser"
            >
              <Link to="add" className="text-light text-decoration-none">
                <BsPlusCircleFill /> Add New User
              </Link>
            </RoleAccessButton>
          </div>
        </div>

        <Row className="d-flex flex-row pb-3">
          <Col xs={12}>
            <Card className="mb-3" style={{backgroundColor: darkMode ? "#212529" : "#fff"}}>
              <Table
                className="my-0"
                error={error}
                isLoading={usersInitialLoading}
                columns={usersColumns}
                darkMode={darkMode}
                data={usersTableData}
                totalCount={usersData?.users?.edges?.length ?? 0}
                noDataText="There are no users to display."
              />

              {deleteUserError != null 
                && <Alert variant="danger" className="mt-3" message="An error occurred while deleting the user. Please try again." />
              }
            </Card>
            <div>
              <Pagination
                pageLoading={usersInitialLoading || usersFetchMoreLoading}
                paginationState={paginationState}
                handleNextPage={handleNextPage}
                handlePrevPage={handlePrevPage}
                handlePageSize={handlePageSize}
                hasNextPage={usersPageInfo?.hasNextPage}
                totalCount={totalCount}
              /> 
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}